import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { EduhintMedialink, EdumaticMedialink, isMedialinkStudentAssignment, Medialink } from '../api/medialink';
import { Module } from '../api/module';
import ModalContext from '../contexts/ModalContext';
import useThrowAsyncError from '../hooks/useThrowAsyncError';
import { ExerciseManager } from './elementis/components/exercise-manager';
import { ExercisePhase } from './elementis/components/exercise-manager/@types/exercise-types';
import { ExerciseTranslationsContextValue } from './elementis/components/exercise-manager/contexts/ExerciseTranslationsContext';

const getTranslations = (t: ReturnType<typeof useTranslation>['t']): ExerciseTranslationsContextValue => ({
  launchModalCompletedSession: {
    title: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.TITLE'),
    message: {
      restartable: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.MESSAGE.RESTARTABLE'),
      nonRestartable: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.MESSAGE.NON_RESTARTABLE'),
    },
    buttons: {
      restart: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.BUTTONS.RESTART'),
      cancel: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.BUTTONS.CANCEL'),
      review: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.BUTTONS.REVIEW'),
    },
    highlightedItem: {
      label: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.LABEL'),
      resultTitle: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.RESULT_TITLE'),
      percentage: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.PERCENTAGE'),
      score: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.SCORE'),
    },
    a11yLabel: t('EXERCISE_MANAGER.LAUNCH_MODAL_COMPLETED_SESSION.A11Y_LABEL'),
  },
  launchModalPausedSession: {
    title: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.TITLE'),
    message: {
      p1: {
        restartable: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.MESSAGE.P1.RESTARTABLE'),
        nonRestartable: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.MESSAGE.P1.NON_RESTARTABLE'),
      },
      p2: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.MESSAGE.P2'),
    },
    buttons: {
      resume: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.BUTTONS.RESUME'),
      restart: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.BUTTONS.RESTART'),
      cancel: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.BUTTONS.CANCEL'),
    },
    highlightedItem: {
      label: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.LABEL'),
      resultTitle: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.RESULT_TITLE'),
      percentage: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.PERCENTAGE'),
      score: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.SCORE'),
      assignmentPill: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.ASSIGNMENT_PILL'),
      deadlinePill: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.DEADLINE_PILL'),
    },
    a11yLabel: t('EXERCISE_MANAGER.LAUNCH_MODAL_PAUSED_SESSION.A11Y_LABEL'),
  },
  progressionModalPausedSession: {
    title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.TITLE'),
    message: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.MESSAGE'),
    buttons: {
      resume: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.BUTTONS.RESUME'),
      restart: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.BUTTONS.RESTART'),
      close: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.BUTTONS.CLOSE'),
    },
    highlightedItem: {
      label: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.LABEL'),
      resultTitle: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.RESULT_TITLE'),
      percentage: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.PERCENTAGE'),
      score: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.SCORE'),
      assignmentPill: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.ASSIGNMENT_PILL'),
      deadlinePill: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.HIGHLIGHTED_ITEM.DEADLINE_PILL'),
    },
    a11yLabel: t('EXERCISE_MANAGER.PROGRESSION_MODAL_PAUSED_SESSION.A11Y_LABEL'),
  },
  closeDialog: t('EXERCISE_MANAGER.CLOSE_DIALOG'),
  serviceNotAvailable: {
    title: t('EXERCISE_MANAGER.SERVICE_NOT_AVAILABLE.TITLE'),
    p1: t('EXERCISE_MANAGER.SERVICE_NOT_AVAILABLE.P1'),
    p2: t('EXERCISE_MANAGER.SERVICE_NOT_AVAILABLE.P2'),
  },
  exerciseAnswersModal: {
    a11yLabel: t('EXERCISE_MANAGER.EXERCISE_ANSWERS_MODAL.A11Y_LABEL'),
  },
  exerciseLoading: {
    title: t('EXERCISE_MANAGER.EXERCISE_LOADING.TITLE'),
    p1: t('EXERCISE_MANAGER.EXERCISE_LOADING.P1'),
    p2: t('EXERCISE_MANAGER.EXERCISE_LOADING.P2'),
  },
  exerciseCalculating: {
    title: t('EXERCISE_MANAGER.EXERCISE_CALCULATING.TITLE'),
    p1: t('EXERCISE_MANAGER.EXERCISE_CALCULATING.P1'),
    p2: t('EXERCISE_MANAGER.EXERCISE_CALCULATING.P2'),
  },
  activeSession: {
    a11yLabel: t('EXERCISE_MANAGER.ACTIVE_SESSION.A11Y_LABEL'),
    buttons: {
      pause: t('EXERCISE_MANAGER.ACTIVE_SESSION.BUTTONS.PAUSE'),
    },
    alerts: {
      retryAction: {
        message: t('EXERCISE_MANAGER.ACTIVE_SESSION.ALERTS.RETRY_ACTION.MESSAGE'),
        buttons: {
          retry: t('EXERCISE_MANAGER.ACTIVE_SESSION.ALERTS.RETRY_ACTION.BUTTONS.RETRY'),
        },
      },
    },
  },
  progressionModalCompletedSession: {
    a11yLabel: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.A11Y_LABEL'),
    title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.TITLE'),
    buttons: {
      close: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.BUTTONS.CLOSE'),
      restart: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.BUTTONS.RESTART'),
      review: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.BUTTONS.REVIEW'),
    },
    highlightedItem: {
      label: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.LABEL'),
      resultTitle: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.RESULT_TITLE'),
      percentage: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.PERCENTAGE'),
      score: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.HIGHLIGHTED_ITEM.SCORE'),
    },
    messages: {
      adaptive: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.ADAPTIVE'),
      veryGood: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.VERY_GOOD'),
      good: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.GOOD'),
      bad: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.BAD'),
      showScore: {
        adaptive: {
          p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.SHOW_SCORE.ADAPTIVE.P1'),
          p2: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.SHOW_SCORE.ADAPTIVE.P2'),
        },
        perfectResult: {
          p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.SHOW_SCORE.PERFECT_RESULT.P1'),
          p2: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.SHOW_SCORE.PERFECT_RESULT.P2'),
        },
        result: {
          p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.SHOW_SCORE.RESULT.P1'),
          p2: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.SHOW_SCORE.RESULT.P2'),
        },
      },
    },
    exerciseModule: {
      insufficient: {
        title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.INSUFFICIENT.TITLE'),
        p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.INSUFFICIENT.P1'),
        step1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.INSUFFICIENT.STEP_1'),
        step1LinkText: t(
          'EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.INSUFFICIENT.STEP_1_LINK_TEXT',
        ),
        step2: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.INSUFFICIENT.STEP_2'),
      },
      sufficient: {
        title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.SUFFICIENT.TITLE'),
        p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.SUFFICIENT.P1'),
        stepsHtml: t(
          'EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.SUFFICIENT.STEPS_HTML',
        ),
      },
      good: {
        title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.GOOD.TITLE'),
        p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.GOOD.P1'),
        stepsHtml: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.GOOD.STEPS_HTML'),
        p2: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.GOOD.P2'),
      },
      veryGood: {
        title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.VERY_GOOD.TITLE'),
        p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.VERY_GOOD.P1'),
        p2: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.VERY_GOOD.P2'),
      },
      perfect: {
        title: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.PERFECT.TITLE'),
        p1: t('EXERCISE_MANAGER.PROGRESSION_MODAL_COMPLETED_SESSION.MESSAGES.EXERCISE_MODULE.PERFECT.P1'),
      },
    },
  },
});

const getAssignmentId = (medialink: Medialink) => {
  if (!isMedialinkStudentAssignment(medialink)) return undefined;

  return medialink.assignments.find(x => x.current)?.id;
};

type Props = {
  id: string;
  medialink: EduhintMedialink | EdumaticMedialink;
  onSessionPaused: () => void;
  onSessionFinished: () => void;
  initialPhase?: ExercisePhase;
  onClose?: () => void;
  selectedModule: Module;
};

export default function ExerciseModal({
  id,
  medialink,
  onSessionPaused,
  onSessionFinished,
  initialPhase,
  onClose,
  selectedModule,
}: Props) {
  const { closeModal } = useContext(ModalContext);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const handleError = useThrowAsyncError();

  const handleClose = () => {
    closeModal(id);
    if (onClose) onClose();
  };

  return (
    <ExerciseManager
      language={language as SupportedUserLanguage}
      medialink={medialink}
      onClose={handleClose}
      onError={handleError}
      onSessionFinished={onSessionFinished}
      onSessionPaused={onSessionPaused}
      translations={getTranslations(t)}
      assignmentId={getAssignmentId(medialink)}
      initialPhase={initialPhase}
      studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
      selectedModule={selectedModule}
    />
  );
}
